import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { pageTitleStyles } from "../../../mui/styles";
import { useCompany } from "../../../contexts/CompanyContext";
import BackLink from "../../shared/BackLink";
import { validateGenInfo, validateContacts } from "./SupplierFormValidation";
import AddBanner from "../Banner/AddBanner";
import {
  SectionAccordian,
  ContactInformationBlock,
  AssistEmailCard,
} from "./EditSupplier.components";
import {
  fetchSupplierStatusses,
  fetchSupplierContactTypes,
  fetchAdminUsers,
  fetchFormData,
  upsertSupplierContacts,
  clearEmailIndex,
  upsertAssistAdmins,
} from "./EditSupplierSB.functions";

/**
 * EditSupplier Component
 * Handles adding and editing suppliers
 * @param {object} props - component props
 * @param {object} props.match - React Router match object
 */
export default function EditSupplier({ match }) {
  const defFormValues = {
    supplier_name: "",
    supplier_description: "",
    supplier_status: 1,
    regex: "",
    order_notes: false,
    order_notes_name: "",
    reports: false,
    reports_name: "",
    custom_page: false,
    custom_page_url: "",
    logo_url: "",
    sales_assist: false,
    sales_assist_text: "",
    agent_assist: false,
    agent_assist_text: "",
    leaderboard: false,
    last_updated: "(now())",
  };

  const defContactForm = {
    contact_email: "",
    contact_first_name: "",
    contact_surname: "",
    contact_number: "",
  };

  const history = useHistory();
  const { upsertSupplier } = useCompany();
  const supplierId = match.params.id;
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(defFormValues);
  const [banner, setBanner] = useState({ images: [] });
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [assistAdmins, setAssistAdmins] = useState([]);
  const [activeSection, setActiveSection] = useState(0);

  const [supplierStatusses, setSupplierStatusses] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [statusMessage, setStatusMessage] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // STATE HANDLERS

  // statusMessage - open and close
  const handleOpenMessage = (message, severity = "info") => {
    setStatusMessage({ open: true, message, severity });
  };

  const handleCloseMessage = () => {
    setStatusMessage({ open: false, message: "", severity: "info" });
  };

  // contacts - handles dynamic contact form values
  const handleContact = (id, key, value) => {
    setContacts(prevContacts =>
      prevContacts.map(item => {
        return item.contact_type_id === id
          ? { ...item, [key]: value }
          : { ...item };
      })
    );
  };

  // activeSection - handles frontend display behaviour for sections/accordians
  const handleActiveSection = section => {
    setActiveSection(prev => (prev === section ? -1 : section));
  };

  // bannerDialogOpen - set open
  const handleBannerDialogOpen = () => {
    setBannerDialogOpen(true);
  };

  // bannerDialogOpen - set close

  const handleBannerDialogClose = () => {
    setBannerDialogOpen(false);
  };

  // assistAdmins - remove a value from the list
  const removeAssistAdmin = (value, type) => {
    const assistAdminsRemovedExist = assistAdmins.filter(adminUser => {
      return !(
        adminUser.value === value &&
        adminUser.refs_supplier_assist_email_type_id === type
      );
    });
    setAssistAdmins(assistAdminsRemovedExist);
  };

  // assistAdmins - add new value if value doesn't exists, remove value if value exists
  const handleAssistEmails = (event, type) => {
    const { value } = event.target;

    // check if value exists in the state
    const adminExist = assistAdmins.filter(
      adminUser =>
        adminUser.value === value &&
        adminUser.refs_supplier_assist_email_type_id === type
    );

    if (adminExist[0]) {
      // remove value
      removeAssistAdmin(value, type);
    } else {
      // add value
      const adminObj = adminList.filter(admin => admin.value === value);
      setAssistAdmins(prev => [
        ...prev,
        { ...adminObj[0], refs_supplier_assist_email_type_id: type },
      ]);
    }
  };

  // formValues - textField and select handler
  const handleTextFields = event => {
    const { name, value } = event.target;
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      [name]: value,
    }));
  };

  // formValues - checkbox handler
  const handleFormValueCheckbox = ({ target }) => {
    const { name } = target;

    setFormValues(prevValues => {
      return { ...prevValues, [name]: !prevValues[name] };
    });
  };

  // HELPERS
  // map contacts
  const contactsSubmitMap = async supId => {
    return contacts.map(item => ({ ...item, supplier_id: supId }));
  };
  // map assist emails
  const assistAdminEmailsMap = async supId => {
    return assistAdmins.map(item => ({
      suppliers_master_id: supId,
      users_master_id: item.value,
      refs_supplier_assist_email_type_id:
        item.refs_supplier_assist_email_type_id,
      is_active: true,
    }));
  };

  // SUBMIT

  const handleSubmit = async () => {
    try {
      // validate formValues
      await validateGenInfo(formValues);

      // validate contacts

      const requiredContactTypeIds = contactTypes
        .filter(type => type.is_required)
        .map(type => type.id);

      const requiredContacts = contacts.filter(contact =>
        requiredContactTypeIds.includes(contact.contact_type_id)
      );

      await validateContacts(requiredContacts);

      // upsert formValues
      const formValuesUpsertResult = await upsertSupplier(formValues);
      if (formValuesUpsertResult.error !== 0) {
        throw new Error(
          `Failed at formValuesUpsertResult: ${formValuesUpsertResult.error}`
        );
      }

      // get supplierId
      const supplierIdReceived = formValuesUpsertResult.data.id;

      // map contacts
      const contactMap = await contactsSubmitMap(supplierIdReceived);

      // upsert contacts
      const contactsUpsertResult = await upsertSupplierContacts(contactMap);
      if (contactsUpsertResult.error !== 0) {
        throw new Error(
          `Failed at contactsUpsertResult: ${contactsUpsertResult.error}`
        );
      }

      // clear assist email index
      const clearAssistAdminEmailResult = await clearEmailIndex(
        supplierIdReceived
      );
      if (clearAssistAdminEmailResult.error !== 0) {
        throw new Error(
          `Failed at clearAssistAdminEmailResult: ${clearAssistAdminEmailResult.error}`
        );
      }

      // map assist emails
      const assistAdminMap = await assistAdminEmailsMap(supplierIdReceived);

      // upsert assist emails
      const upsertAssistAdminResult = await upsertAssistAdmins(assistAdminMap);
      if (upsertAssistAdminResult.error !== 0) {
        throw new Error(
          `Failed at assistAdminResult: ${upsertAssistAdminResult.error}`
        );
      }

      if (supplierId) {
        handleOpenMessage("Supplier Updated", "success");
        history.push(`/admin/Supplier/${supplierId}`);
      } else {
        handleOpenMessage("Supplier Created", "success");
        history.push("/settings/suppliers/");
      }
    } catch (error) {
      console.error("Error in training sequence:", error);
      handleOpenMessage(error, "error");
    }
  };

  // LOCAL COMPONENTS

  const supplierStatusMenuItems = supplierStatusses.map(item => (
    <MenuItem key={item.value} value={item.value}>
      {item.label}
    </MenuItem>
  ));

  const adminSelectMenuItems = adminList.map(admin => (
    <MenuItem value={admin.value}>{admin.label}</MenuItem>
  ));

  const AssistAdminMap = ({ type }) =>
    assistAdmins
      .filter(admin => admin.refs_supplier_assist_email_type_id === type)
      .map(admin => (
        <AssistEmailCard
          adminUser={admin}
          removeAssistAdmin={removeAssistAdmin}
          type={type}
        />
      ));

  // SIDE EFFECTS
  useEffect(async () => {
    setLoading(true);
    // supplier statusses
    const { data: supplierStatussesData, error: supplierStatussesError } =
      await fetchSupplierStatusses();

    if (supplierStatussesError === 0) {
      setSupplierStatusses(
        supplierStatussesData.map(status => ({
          value: status.id,
          label: status.status,
        }))
      );
    }

    // supplier contact types
    const { data: supplierContactTypesData, error: supplierContactTypesError } =
      await fetchSupplierContactTypes();

    if (supplierContactTypesError === 0) {
      setContactTypes(supplierContactTypesData);
    }

    // admin users
    const { data: adminUsersData, error: adminUsersError } =
      await fetchAdminUsers();

    if (adminUsersError === 0) {
      const adminUsersDataMap = adminUsersData.map(admin => ({
        value: admin.id,
        label: `Z-${admin.id}: ${admin.first_name} ${admin.surname}`,
      }));
      setAdminList(adminUsersDataMap);
    }

    // form data if supplierId exists
    if (supplierId) {
      const { data: formData, error: formError } = await fetchFormData(
        supplierId
      );

      if (formError === 0) {
        const { suppliers_assist_email_index, suppliers_contact_detail } =
          formData;

        const supplierData = _.omit(formData, [
          "suppliers_assist_email_index",
          "suppliers_contact_detail",
        ]);

        const suppliersAssistEmailMap = suppliers_assist_email_index.map(
          admin => {
            const { users_master } = admin;

            return {
              value: admin.users_master_id,
              label: `Z-${users_master.id}: ${users_master.first_name} ${users_master.surname}`,
              refs_supplier_assist_email_type_id:
                admin.refs_supplier_assist_email_type_id,
            };
          }
        );

        setFormValues(supplierData);
        setContacts(suppliers_contact_detail);
        setAssistAdmins(suppliersAssistEmailMap);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    setContacts(
      contactTypes?.map(type => ({
        ...defContactForm,
        contact_type_id: type.id,
      }))
    );
  }, [contactTypes]);

  return (
    <div>
      <Dialog open={bannerDialogOpen} onClose={handleBannerDialogClose}>
        <DialogTitle>Add Banner</DialogTitle>
        <DialogContent>
          <AddBanner
            open={bannerDialogOpen}
            close={handleBannerDialogClose}
            banner={banner}
            setBanner={setBanner}
            folder={match.params.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBannerDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleBannerDialogClose} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <BackLink />
      <h1 style={pageTitleStyles}>
        {supplierId ? "Edit Supplier" : "Add Supplier"}
      </h1>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Grid container xs={12} spacing={3}>
          <SectionAccordian
            activeId={activeSection}
            heading="General Information"
            id={0}
            handleAccordianExpanded={handleActiveSection}
          >
            <Grid item xs={12}>
              <TextField
                label="Supplier Name"
                placeholder="Supplier Name"
                variant="outlined"
                required
                name="supplier_name"
                value={formValues.supplier_name}
                onChange={handleTextFields}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Supplier Description"
                variant="outlined"
                required
                name="supplier_description"
                value={formValues.supplier_description}
                onChange={handleTextFields}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Logo URL"
                variant="outlined"
                required
                name="logo_url"
                value={formValues.logo_url}
                onChange={handleTextFields}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel id="supplier-status-label">
                  Supplier Status
                </InputLabel>
                <Select
                  value={formValues.supplier_status}
                  fullWidth
                  variant="outlined"
                  required
                  name="supplier_status"
                  onChange={handleTextFields}
                  label="Supplier Status"
                >
                  {supplierStatusMenuItems}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Order Number Regex Value"
                variant="outlined"
                name="regex"
                value={formValues.regex}
                onChange={handleTextFields}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleBannerDialogOpen}
              >
                Add Banner
              </Button>
            </Grid>
          </SectionAccordian>
          <SectionAccordian
            activeId={activeSection}
            handleAccordianExpanded={handleActiveSection}
            id={1}
            heading="Contact Information"
          >
            {contactTypes.map(infoType => (
              <ContactInformationBlock
                informationType={infoType}
                contacts={contacts}
                handleContact={handleContact}
              />
            ))}
          </SectionAccordian>
          <SectionAccordian
            activeId={activeSection}
            heading="Attributes"
            id={2}
            handleAccordianExpanded={handleActiveSection}
          >
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginBottom: 10 }}
                control={<Checkbox required />}
                label="Leaderboard"
                name="leaderboard"
                checked={formValues.leaderboard}
                onChange={handleFormValueCheckbox}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginBottom: 10 }}
                control={<Checkbox required />}
                label="Order Notes"
                name="order_notes"
                checked={formValues.order_notes}
                onChange={handleFormValueCheckbox}
              />
            </Grid>
            {formValues.order_notes === true ? (
              <Grid item xs={12}>
                <TextField
                  label="Order Notes Name"
                  variant="outlined"
                  name="order_notes_name"
                  required
                  value={formValues.order_notes_name}
                  onChange={handleTextFields}
                  fullWidth
                />
              </Grid>
            ) : (
              <div />
            )}
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginBottom: 10 }}
                control={<Checkbox required />}
                label="Reports"
                name="reports"
                checked={formValues.reports}
                onChange={handleFormValueCheckbox}
              />
            </Grid>
            {formValues.reports === true ? (
              <Grid item xs={12}>
                <TextField
                  label="Report Name"
                  variant="outlined"
                  name="reports_name"
                  required
                  value={formValues.reports_name}
                  onChange={handleTextFields}
                  fullWidth
                />
              </Grid>
            ) : (
              <div />
            )}
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginBottom: 10 }}
                control={<Checkbox required />}
                label="Custom page"
                name="custom_page"
                checked={formValues.custom_page}
                onChange={handleFormValueCheckbox}
              />
            </Grid>
            {formValues.custom_page === true ? (
              <Grid item xs={12}>
                <TextField
                  label="Custom Page Url"
                  variant="outlined"
                  name="custom_page_url"
                  required
                  value={formValues.custom_page_url}
                  onChange={handleTextFields}
                  fullWidth
                />
              </Grid>
            ) : (
              <div />
            )}
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginBottom: 10 }}
                control={<Checkbox required />}
                label="Sales Assist"
                name="sales_assist"
                checked={formValues.sales_assist}
                onChange={handleFormValueCheckbox}
              />
            </Grid>
            {formValues.sales_assist && (
              <Grid container spacing={3} style={{ margin: 3 }}>
                {" "}
                <Grid item xs={12}>
                  <TextField
                    label="Sales Assist front end display notes (MD)"
                    variant="outlined"
                    name="sales_assist_text"
                    value={formValues.sales_assist_text}
                    onChange={handleTextFields}
                    multiline
                    fullWidth
                  />{" "}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel id="supplier-status-label">
                      Select Admins Responsible for this Role
                    </InputLabel>
                    <Select
                      fullWidth
                      variant="outlined"
                      required
                      name="sales_assist_admin"
                      onChange={event => handleAssistEmails(event, 1)}
                      label="Select Admins Responsible for this Role"
                      value={{ label: "Select Admin", value: -1 }}
                    >
                      {adminSelectMenuItems}
                    </Select>
                  </FormControl>
                </Grid>{" "}
                <Grid
                  container
                  style={{ marginLeft: 10, padding: 0, marginBottom: 10 }}
                >
                  <AssistAdminMap type={1} />
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                style={{ marginBottom: 10 }}
                control={<Checkbox required />}
                label="Agent Support"
                name="agent_assist"
                checked={formValues.agent_assist}
                onChange={handleFormValueCheckbox}
              />
            </Grid>
            {formValues.agent_assist && (
              <Grid container spacing={3} style={{ margin: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Agent Support front end display notes (MD)"
                    variant="outlined"
                    name="agent_assist_text"
                    value={formValues.agent_assist_text}
                    onChange={handleTextFields}
                    multiline
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel id="supplier-status-label">
                      Select Admins Responsible for this Role
                    </InputLabel>
                    <Select
                      fullWidth
                      variant="outlined"
                      required
                      name="sales_assist_admin"
                      onChange={event => handleAssistEmails(event, 2)}
                      label="Select Admins Responsible for this Role"
                      value={{ label: "Select Admin", value: -1 }}
                    >
                      {adminSelectMenuItems}
                    </Select>
                  </FormControl>
                </Grid>{" "}
                <Grid
                  container
                  style={{ marginLeft: 10, padding: 0, marginBottom: 10 }}
                >
                  <AssistAdminMap type={2} />
                </Grid>
              </Grid>
            )}
          </SectionAccordian>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={statusMessage.open}
        onClose={handleCloseMessage}
        autoHideDuration={3000}
      >
        <Alert severity={statusMessage.severity}>{statusMessage.message}</Alert>
      </Snackbar>
    </div>
  );
}
